import type { ForegroundPush, PushPermission, PushService, Unsubscribe } from '@mendikot/app-core';
import { paths, PUSH_TOPICS, type Reply } from '@mendikot/protocol';
import type { FirebaseApp } from 'firebase/app';
import { loadOnline } from './firebase';

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Web push via FCM (plan §7). The token is registered against the PWA service worker, stored under
 * /users/{uid}/fcmTokens, and topics are subscribed server-side (web cannot subscribe directly).
 */
export class WebPush implements PushService {
  private readonly listeners = new Set<(p: ForegroundPush) => void>();
  private wired = false;

  constructor(
    private readonly app: FirebaseApp | null,
    private readonly syncTopics: (token: string, topics: string[]) => Promise<Reply>,
    private readonly lang: () => string,
  ) {}

  async supported(): Promise<boolean> {
    if (!this.app || !import.meta.env.VITE_FIREBASE_VAPID_KEY || !('serviceWorker' in navigator))
      return false;
    const { isSupported } = await import('firebase/messaging');
    return isSupported();
  }

  permission(): PushPermission {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  }

  async enable(uid: string): Promise<PushPermission> {
    if (!(await this.supported())) return 'unsupported';
    const result =
      Notification.permission === 'default'
        ? await Notification.requestPermission()
        : Notification.permission;
    if (result !== 'granted') return result;
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
    const messaging = getMessaging(this.app!);
    const registration = await navigator.serviceWorker.ready;
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    const tokenId = (await sha256Hex(token)).slice(0, 24);
    const [{ db }, { ref, serverTimestamp, set }] = await Promise.all([
      loadOnline(),
      import('firebase/database'),
    ]);
    await set(ref(db, paths.fcmToken(uid, tokenId)), {
      token,
      platform: 'web',
      lang: this.lang(),
      updatedAt: serverTimestamp(),
    });
    await this.syncTopics(token, [
      PUSH_TOPICS.announcements,
      PUSH_TOPICS.festivals,
      PUSH_TOPICS.lang(this.lang()),
    ]);
    if (!this.wired) {
      this.wired = true;
      onMessage(messaging, (payload) => {
        const push: ForegroundPush = {
          ...(payload.data as Record<string, string> | undefined),
          title: payload.notification?.title,
          body: payload.notification?.body,
        };
        for (const listener of this.listeners) listener(push);
      });
    }
    return 'granted';
  }

  onForeground(listener: (p: ForegroundPush) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
